import $ from 'jquery';
import Cache from 'next/javascripts/modules/cache';
import simpleUrl from 'simple-url';
import { closest } from 'next/javascripts/modules/dom';
import getLoadingElement from 'next/javascripts/modules/page-loading';

const pageCache = new Cache();

function formatURL(url = window.location.href) {
  return simpleUrl(url).toString('relative');
}

export default {
  properties: {
    opened: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: true,
    },

    baseUrl: {
      type: String,
    },

    containerSelector: {
      type: String,
    },
  },

  ready() {
    if (!this.baseUrl) this.baseUrl = window.location.href;
    if (this.containerSelector) {
      this.container = document.querySelector(this.containerSelector);
    }

    if (this.container) {
      this.listenTo(this.container, {
        click: this.handleContainerClick.bind(this),
        'tao:openpage': this.handleOpenpageEvent.bind(this),
      });
    }

    this.listenTo(this, {
      [`${this.$tag}:closed`]: this.handlePageClosed.bind(this),
      click: this.handlePageClick.bind(this),
      'tao:openpage': this.handleOpenpageEvent.bind(this),
    });
  },

  handleOpenpageEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    const { url, action } = event.detail;
    this.requestPage(url, action);
  },

  handleContainerClick(event) {
    const pageLink = closest(event.target, '[data-page-url]', this.container);
    if (!pageLink) return;
    event.preventDefault();
    event.stopPropagation();

    const { pageUrl, pageAction } = pageLink.dataset;
    this.requestPage(
      pageUrl,
      (event.ctrlKey || event.metaKey) ? 'newtab' : pageAction,
    );
  },

  handlePageClick(event) {
    const { target } = event;
    const stackLink = closest(target, 'a[data-stack]', this);
    const pageLink = closest(target, '[data-page-url]', this);

    if (stackLink) {
      window.open(target.getAttribute('href'));
    } else if (pageLink) {
      const { pageUrl, pageAction } = pageLink.dataset;
      this.requestPage(
        pageUrl,
        (event.ctrlKey || event.metaKey) ? 'newtab' : pageAction,
      );
    } else if (closest(target, '.page-close-button')) {
      this.close();
    } else {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  },

  handlePageClosed(event) {
    this.opened = false;

    if (this.baseTitle) {
      document.title = this.baseTitle;
      this.baseTitle = null;
    }

    this.setPageCache();
    this.setContent();

    setTimeout(() => {
      if (['statechange', 'disconnected'].indexOf(event.detail.action) === -1) {
        window.history.pushState(null, '', this.baseUrl);
      }
    });
  },

  requestPage(url, action) {
    if (!url) return;
    if (this.opened) this.setPageCache();

    if (action === 'replace') {
      window.history.replaceState(null, '', url);
    } else if (action === 'newtab') {
      window.open(url);
      return;
    } else {
      window.history.pushState(null, '', url);
    }

    const loading = getLoadingElement(url);
    this.setContent(loading.outerHTML);

    this.opened = true;
    this.open();
    this.request(url);
  },

  request(url) {
    if (this.requestXHR) this.requestXHR.abort();

    const cache = pageCache.get(formatURL(url));
    if (cache) {
      this.disabled = true;
      this.setContent(cache);
    }

    this.requestXHR = $.ajax({
      url,
      method: 'GET',
      dataType: 'script',
      complete: () => {
        this.disabled = false;
        this.requestXHR = null;
      },
    });
  },

  loadPage(content, options = {}) {
    this.setContent(content);

    if (options.title) {
      this.baseTitle = document.title;
      document.title = options.title;
    }

    if (window.tower && window.tower.stack) {
      // https://tower.im/teams/630713/todos/54117
      const { currentPage } = window.tower.stack;
      const state = {
        name: document.title,
        url: formatURL(),
      };
      currentPage.setCache({ ...state, html: currentPage.el.html() });
      currentPage.trigger('replacestate', [state]);
      window.history.replaceState(state, state.name, state.url);
    }

    setTimeout(() => {
      this.namespacedTrigger('pageLoaded', { bubbles: true });
    });
  },

  setPageCache(url = '') {
    const content = this.querySelector('.page__content').innerHTML;
    if (content) pageCache.set(formatURL(url), content);
  },

  disconnected() {
    if (this.requestXHR) this.requestXHR.abort();
    this.stopListening();
  },
};
